import { useEffect } from 'react';
import { getTabId } from './tabId';
import type { SessionKillReason } from '../api/client';

/**
 * Opens ONE EventSource to /api/auth/session-stream while authenticated and
 * invokes onKill(reason) when the server pushes a `terminate`. A network drop is
 * treated as transient (reconnect with backoff + jitter, reusing the SAME tabId)
 * — never as a logout. The stream is the INSTANT path for cross-device logout;
 * the per-request 401 in the API client is the guaranteed backstop.
 */
export function useSessionStream(
  enabled: boolean,
  onKill: (reason: SessionKillReason) => void,
): void {
  useEffect(() => {
    if (!enabled) return;

    let closed = false;
    let es: EventSource | null = null;
    let backoff = 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const open = (): void => {
      if (closed) return;
      es = new EventSource(`/api/auth/session-stream?tabId=${encodeURIComponent(getTabId())}`);

      es.addEventListener('ready', () => {
        backoff = 1000; // healthy connection — reset backoff
      });

      es.addEventListener('terminate', (ev) => {
        let reason: SessionKillReason = 'logged_out';
        try {
          const data = JSON.parse((ev as MessageEvent).data) as { reason?: SessionKillReason };
          if (data?.reason) reason = data.reason;
        } catch {
          /* default */
        }
        closed = true;
        es?.close();
        onKill(reason);
      });

      es.onerror = (): void => {
        // Transient drop (server restart, network blip, IP change closing the
        // socket). Reconnect with exponential backoff + jitter; do NOT log out.
        es?.close();
        if (closed) return;
        if (timer) clearTimeout(timer);
        const jitter = 0.8 + Math.random() * 0.4;
        timer = setTimeout(open, Math.min(backoff, 30_000) * jitter);
        backoff = Math.min(backoff * 2, 30_000);
      };
    };

    open();

    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      es?.close();
    };
  }, [enabled, onKill]);
}
